import { ApolloError } from '@apollo/client';
import { Routes } from 'src/lib/constants';
import { AppThunk } from 'src/redux/store';
import { buildQueryString } from 'src/utils/query-string';
import {
  getAuth0State,
  getAuth0Variant,
  getExcludeInstitution,
  getExperimentOverrides,
  getInputs,
  getIsMobileApp,
  getIsSignedIn,
} from '../selectors';
import type { InputsReducer } from 'src/redux/reducers/inputs';
import type { AllInputs } from 'src/types/inputs';
import { Auth0Analytics } from 'src/components/screens/auth0';
import { Screen } from 'src/lib/flows/screens';
import { CCIN_AUTH0 } from 'src/lib/experiments';
import LinkGenerator from '@nerdwallet/nw-auth-links';

export const buildResultsUrl =
  ({
    formSubmitId,
    saveInputResponse,
    inputsOverride,
    error,
    auth0Analytics,
    redirectType,
    windowType,
  }: {
    formSubmitId: string;
    saveInputResponse: boolean;
    inputsOverride?: InputsReducer;
    error?: ApolloError;
    auth0Analytics?: Auth0Analytics;
    redirectType?: string;
    windowType?: string;
  }): AppThunk<string> =>
  (dispatch, getState) => {
    const state = getState();
    const isMobileApp = getIsMobileApp(state);
    const isSignedIn = getIsSignedIn(state);
    const auth0Variant = getAuth0Variant(state);
    const hasAuth0State = getAuth0State(state);
    const useAuth0 = !isMobileApp && !isSignedIn && !hasAuth0State;

    if (isMobileApp) {
      console.log(
        `Calling buildResultsUrl() with params: ${JSON.stringify({
          formSubmitId,
          saveInputResponse,
          inputsOverride,
          error,
        })}`
      );
    }

    let currentInputs = getInputs(state) as Partial<AllInputs> | never[];

    if (isMobileApp) {
      console.log(`currentInputs: ${JSON.stringify(currentInputs)}`);
    }

    if (!isMobileApp && !useAuth0) {
      currentInputs = !saveInputResponse
        ? inputsOverride
          ? inputsOverride
          : getInputs(state)
        : [];
    }

    const deeplinkSubflows = [
      'ccin-814_new_big_purchase_flow_experiment',
      'CCIN-1354_cardfinder_build_credit_experiment',
    ];

    const experimentsForURL = Object.entries(state.experiments ?? {}).reduce(
      (acc, [experimentKey, value]) => {
        if (deeplinkSubflows.includes(experimentKey) && value.variant !== '') {
          acc[`test_${experimentKey}`] = value.variant;
        }
        return acc;
      },
      {} as Record<string, string>
    );

    const queryStringParams = {
      ...(error || currentInputs),
      ...getExperimentOverrides(state),
      ...experimentsForURL,
      formSubmitId,
      exclude_institution: getExcludeInstitution(state),
      trk_source: isMobileApp ? 'mobile' : 'web',
    };

    if (isMobileApp) {
      console.log(`Build query string: ${JSON.stringify(queryStringParams)}`);
    }

    const queryString = buildQueryString(queryStringParams);
    const returnToLoadingUrl = `${Routes.Flow}/${
      Screen.Loading
    }${queryString}&anonymousId=${auth0Analytics?.anonymousUserId}&appState=${
      auth0Variant ?? CCIN_AUTH0.variant.CONTROL
    }${windowType ? `_${windowType}` : ''}`;

    if (redirectType === 'skip') {
      return returnToLoadingUrl;
    }

    //setting the redirect host temporarily for QA
    const auth0RedirectUrl = `${window.location.protocol}//${window.location.host}${returnToLoadingUrl}`;

    if (auth0Analytics && useAuth0) {
      const linkGenerator = new LinkGenerator();
      const regLink = linkGenerator.register({
        redirectTo: auth0RedirectUrl,
        driverPageName: auth0Analytics.driverPageName,
        driverLocation: 'cardfinder_flow',
        deployableName: auth0Analytics.deployableName,
        deployableVersion: auth0Analytics.deployableVersion,
        driverPageUrl: auth0Analytics.driverPageUrl,
        driverPageViewId: auth0Analytics.driverPageViewId,
        driverContentSource: '',
        anonymousUserId: auth0Analytics.anonymousUserId,
        mostRecentTraditionalDeviceId:
          auth0Analytics.mostRecentTraditionalDeviceId,
        driverContentId: '',
        driverElement: '',
        driverFlowName: '',
        driverSubLocation: '',
        cancelUrl: auth0RedirectUrl,
        cancelCta: 'No, let me see my results',
      });
      return `${process.env.NEXT_PUBLIC_NW_URL}${regLink}`;
    }
    const redirectUrl = `${Routes.Results}${queryString}`;
    return redirectUrl;
  };

export const terminateQuizFlow =
  (
    formSubmitId: string,
    saveInputResponse: boolean,
    error?: ApolloError
  ): AppThunk =>
  (dispatch, getState) => {
    const resultsUrl = dispatch(
      buildResultsUrl({
        formSubmitId,
        saveInputResponse,
        error,
      })
    );

    console.log(`Redirecting user in terminateQuizFlow() to ${resultsUrl}`);

    window.location.assign(resultsUrl);
  };
